import styled from 'styled-components';

import colours from 'theme/atoms/colours';
import shadows from 'theme/atoms/shadows';
import breakpoints from 'theme/atoms/breakpoints';
import * as Text from 'theme/components/Text.style';
import * as RechartsStyled from 'dashboard/styles/Recharts.style';

export const HeatmapLayout = styled.div`
  display: flex;
  position: relative;
  gap: 20px;
  width: 100%;

  @media (max-width: ${breakpoints.tablet}) {
    flex-direction: column;
    gap: 15px;
  }
`;

export const GridScroller = styled.div`
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;

  @media (max-width: ${breakpoints.tablet}) {
    padding-bottom: 10px;
  }
`;

export const Grid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: auto repeat(${({ $columns }) => $columns}, 1fr);
  gap: 6px;
  min-width: 520px;

  @media (max-width: ${breakpoints.tablet}) {
    gap: 4px;
    min-width: 280px;
  }
`;

export const RowLabel = styled(Text.LabelSemibold)`
  align-items: center;
  background: ${colours.shade[0]};
  color: ${colours.neutral[900]};
  display: flex;
  justify-content: flex-end;
  left: 0;
  padding-right: 10px;
  position: sticky;
  white-space: nowrap;
  z-index: 1;

  @media (max-width: ${breakpoints.tablet}) {
    padding-right: 6px;
  }
`;

export const ColumnLabel = styled(Text.LabelSemibold)`
  color: ${colours.neutral[900]};
  padding-top: 6px;
  text-align: center;
`;

export type CellState = 'idle' | 'active' | 'dimmed';

export const Cell = styled.div<{
  $background: string;
  $state: CellState;
  $isFuture: boolean;
}>`
  align-items: center;
  background: ${({ $background, $isFuture }) =>
    $isFuture ? colours.neutral[100] : $background};
  border-radius: 8px;
  box-shadow: ${({ $state }) => ($state === 'active' ? shadows.M : 'none')};
  cursor: default;
  opacity: ${({ $state, $isFuture }) => {
    if ($state === 'dimmed') return 0.35;
    return $isFuture ? 0.5 : 1;
  }};
  outline: none;
  position: relative;
  transform: ${({ $state }) => ($state === 'active' ? 'scale(1.08)' : 'none')};
  transition: opacity 150ms ease, transform 150ms ease, box-shadow 150ms ease;
  z-index: ${({ $state }) => ($state === 'active' ? 2 : 0)};

  &:focus-visible {
    outline: 2px solid ${colours.primary[500]};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
  display: flex;
  justify-content: center;
  min-height: 30px;
  padding: 4px;

  @media (max-width: ${breakpoints.tablet}) {
    border-radius: 6px;
    min-height: 26px;
    padding: 2px;
  }
`;

export const CellTooltip = styled(RechartsStyled.Tooltip)`
  gap: 4px;
  pointer-events: none;
  position: absolute;
  transform: translateX(-50%);
  white-space: nowrap;
  z-index: 3;

  > p {
    margin-bottom: 0;
  }

  > p::first-letter {
    text-transform: uppercase;
  }
`;

export const CellValue = styled(Text.Label)`
  color: ${colours.neutral[700]};
`;

export const Legend = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  gap: 8px;

  @media (max-width: ${breakpoints.tablet}) {
    column-gap: 8px;
    display: grid;
    grid-template-areas:
      'title title title'
      'min scale max';
    grid-template-columns: auto 1fr auto;
    row-gap: 6px;
  }
`;

export const LegendBarColumn = styled.div`
  align-items: center;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: 6px;

  @media (max-width: ${breakpoints.tablet}) {
    display: contents;
  }
`;

export const LegendScale = styled.div<{
  $verticalGradient: string;
  $horizontalGradient: string;
}>`
  background: ${({ $verticalGradient }) => $verticalGradient};
  border-radius: 6px;
  flex: 1;
  min-height: 200px;
  width: 12px;

  @media (max-width: ${breakpoints.tablet}) {
    background: ${({ $horizontalGradient }) => $horizontalGradient};
    grid-area: scale;
    height: 10px;
    min-height: 0;
    width: 100%;
  }
`;

const LegendTick = styled(Text.Label)`
  color: ${colours.neutral[500]};
`;

export const LegendMaxTick = styled(LegendTick)`
  @media (max-width: ${breakpoints.tablet}) {
    grid-area: max;
  }
`;

export const LegendMinTick = styled(LegendTick)`
  @media (max-width: ${breakpoints.tablet}) {
    grid-area: min;
  }
`;

export const LegendTitle = styled(Text.Label)`
  color: ${colours.neutral[500]};
  transform: rotate(180deg);
  writing-mode: vertical-rl;

  @media (max-width: ${breakpoints.tablet}) {
    grid-area: title;
    transform: none;
    writing-mode: horizontal-tb;
  }
`;
